import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import socket from '../utils/socket';
import BlueBuildingLogo from '../assets/img/bluebuilding-blue.svg';
import './UniversalCamViewer.css';

const UniversalCamViewer = ({ buildingId, camId, className, webrtcUrl }) => {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const mediaSourceRef = useRef(null);
  const sourceBufferRef = useRef(null);
  const queueRef = useRef([]);
  const [streamMode, setStreamMode] = useState('jpeg');
  const [hasFeed, setHasFeed] = useState(false);

  useEffect(() => {
    if (!buildingId || !camId) return;

    let isMounted = true;
    const targetWhepUrl = webrtcUrl || null;

    const cleanupWebRTC = () => {
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        if (stream.getTracks) {
          stream.getTracks().forEach((track) => track.stop());
        }
        videoRef.current.srcObject = null;
      }
      setHasFeed(false);
    };

    const channel = `cam_streaming_${camId}`;

    let isProcessing = false;

    const handleData = async (data) => {
      if (!data || isProcessing) return; // Descartar fotogramas si el renderizador aún está procesando el anterior (evita desfase)
      const bytes = new Uint8Array(data);
      if (bytes.length < 2) return;

      const isJpeg = bytes[0] === 0xFF && bytes[1] === 0xD8;

      if (isJpeg) {
        if (streamMode !== 'jpeg') {
          setStreamMode('jpeg');
        }

        const canvas = canvasRef.current;
        if (!canvas) return;

        isProcessing = true;
        try {
          const blob = new Blob([bytes], { type: 'image/jpeg' });
          if (window.createImageBitmap) {
            const bitmap = await createImageBitmap(blob);
            if (canvasRef.current) {
              if (canvasRef.current.width !== bitmap.width || canvasRef.current.height !== bitmap.height) {
                canvasRef.current.width = bitmap.width;
                canvasRef.current.height = bitmap.height;
              }
              const ctx = canvasRef.current.getContext('2d');
              ctx.drawImage(bitmap, 0, 0);
              setHasFeed(true);
            }
            bitmap.close();
          } else {
            const url = URL.createObjectURL(blob);
            const img = new Image();
            img.onload = () => {
              if (canvasRef.current) {
                canvasRef.current.width = img.width;
                canvasRef.current.height = img.height;
                const ctx = canvasRef.current.getContext('2d');
                ctx.drawImage(img, 0, 0);
                setHasFeed(true);
              }
              URL.revokeObjectURL(url);
              isProcessing = false;
            };
            img.onerror = () => {
              URL.revokeObjectURL(url);
              isProcessing = false;
            };
            img.src = url;
            return;
          }
        } catch (e) {
          // Ignorar descartes menores de cuadros corruptos
        } finally {
          if (window.createImageBitmap) {
            isProcessing = false;
          }
        }
      } else {
        if (streamMode !== 'video') {
          setStreamMode('video');
        }

        const video = videoRef.current;
        if (!video) return;

        if (!mediaSourceRef.current && window.MediaSource) {
          const ms = new MediaSource();
          mediaSourceRef.current = ms;
          video.src = URL.createObjectURL(ms);

          ms.addEventListener('sourceopen', () => {
            try {
              const mime = MediaSource.isTypeSupported('video/mp4; codecs="avc1.42E01E"')
                ? 'video/mp4; codecs="avc1.42E01E"'
                : 'video/mp4';
              const sb = ms.addSourceBuffer(mime);
              sourceBufferRef.current = sb;

              sb.addEventListener('updateend', () => {
                if (queueRef.current.length > 0 && !sb.updating) {
                  sb.appendBuffer(queueRef.current.shift());
                }
              });
            } catch (err) {
              console.error('Error adding sourceBuffer:', err);
            }
          });
        }

        const sb = sourceBufferRef.current;
        if (sb) {
          if (!sb.updating) {
            try {
              sb.appendBuffer(bytes);
            } catch (err) {
              queueRef.current.push(bytes);
            }
          } else {
            queueRef.current.push(bytes);
            if (queueRef.current.length > 10) {
              queueRef.current.shift();
            }
          }
        }
      }
    };

    const cleanupSocket = () => {
      socket.emit('leave:cam_stream', { buildingId, camId });
      socket.off(channel, handleData);

      if (mediaSourceRef.current) {
        if (mediaSourceRef.current.readyState === 'open') {
          try { mediaSourceRef.current.endOfStream(); } catch (e) {}
        }
        mediaSourceRef.current = null;
      }
      sourceBufferRef.current = null;
      queueRef.current = [];
      if (videoRef.current && videoRef.current.src) {
        URL.revokeObjectURL(videoRef.current.src);
        videoRef.current.src = '';
      }
      setHasFeed(false);
    };

    const startSocketStream = () => {
      socket.emit('subscribe:cam_stream', { buildingId, camId });
      socket.on(channel, handleData);
    };

    const startWebRTCStream = async (url) => {
      try {
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        });
        pcRef.current = pc;

        pc.addTransceiver('video', { direction: 'recvonly' });

        pc.ontrack = (event) => {
          if (!isMounted) return;
          if (videoRef.current && event.streams && event.streams[0]) {
            videoRef.current.srcObject = event.streams[0];
            setStreamMode('video');
            setHasFeed(true);
          }
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const token = localStorage.getItem('authToken');
        const headers = { 'Content-Type': 'application/sdp' };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const endpoint = token && !url.includes('token=')
          ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
          : url;

        const res = await fetch(endpoint, {
          method: 'POST',
          body: offer.sdp,
          headers,
        });

        if (!res.ok) {
          throw new Error(`WHEP error ${res.status}`);
        }

        const answerSdp = await res.text();
        if (isMounted && pcRef.current) {
          await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
        }
      } catch (err) {
        console.warn('[UniversalCamViewer] Fallback to socket stream:', err.message);
        if (isMounted) {
          cleanupWebRTC();
          startSocketStream();
        }
      }
    };

    if (targetWhepUrl) {
      startWebRTCStream(targetWhepUrl);
    } else {
      startSocketStream();
    }

    return () => {
      isMounted = false;
      cleanupWebRTC();
      cleanupSocket();
    };
  }, [buildingId, camId, streamMode, webrtcUrl]);

  const isJpegVisible = streamMode === 'jpeg' && hasFeed;
  const isVideoVisible = streamMode === 'video' && hasFeed;

  return (
    <div className="universal-cam-wrapper">
      <canvas
        ref={canvasRef}
        className={`${className || 'cam-canvas'} universal-cam-media ${isJpegVisible ? 'is-visible' : 'is-hidden'}`}
      />
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`${className || 'cam-canvas'} universal-cam-media ${isVideoVisible ? 'is-visible' : 'is-hidden'}`}
      />
      {!hasFeed && (
        <div className="universal-cam-placeholder">
          <img
            src={BlueBuildingLogo}
            alt="BlueBuilding"
            className="universal-cam-placeholder-logo"
          />
        </div>
      )}
    </div>
  );
};

UniversalCamViewer.propTypes = {
  buildingId: PropTypes.string.isRequired,
  camId: PropTypes.string.isRequired,
  className: PropTypes.string,
  webrtcUrl: PropTypes.string,
};

export default UniversalCamViewer;
